import type { ReactNode } from 'react';

export type ToastVariant = 'info' | 'success' | 'error';

export interface ToastOptions {
	title: ReactNode;
	description?: ReactNode;
	variant?: ToastVariant;
	/** Auto-dismiss after this many ms. Defaults to 5000. */
	duration?: number;
	/** An optional action button (e.g. Undo); the toast dismisses when it is pressed. */
	action?: {
		label: string;
		onClick: () => void;
	};
}

export interface ToastRecord extends ToastOptions {
	id: number;
}

type Listener = (toasts: ToastRecord[]) => void;

/**
 * A tiny client-side pub/sub for toasts, deliberately outside React so code that
 * isn't in the tree — notably the React Query global `onError` — can raise one.
 * The `ToastProvider` subscribes and renders. (Client only; never called in SSR.)
 */
let toasts: ToastRecord[] = [];
let nextId = 1;
const listeners = new Set<Listener>();

function emit(): void {
	for (const listener of listeners) {
		listener(toasts);
	}
}

export function subscribeToasts(listener: Listener): () => void {
	listeners.add(listener);
	listener(toasts);
	return () => {
		listeners.delete(listener);
	};
}

/** Raise a toast; returns its id so it can be dismissed programmatically. */
export function toast(options: ToastOptions): number {
	const id = nextId++;
	toasts = [...toasts, { ...options, id }];
	emit();
	return id;
}

export function dismissToast(id: number): void {
	toasts = toasts.filter((entry) => entry.id !== id);
	emit();
}

/**
 * Turn a failed request into an error toast — wire this into `QueryProvider`'s
 * `onError` for one-line global failure feedback.
 */
export function toastApiError(error: unknown): void {
	const raw =
		typeof error === 'object' && error !== null && 'message' in error
			? (error as { message: unknown }).message
			: undefined;
	// Only use a real, non-empty string; otherwise a `{ message: undefined }`
	// error would surface the literal text "undefined".
	const message = typeof raw === 'string' && raw.length > 0 ? raw : 'Something went wrong';
	toast({ variant: 'error', title: message });
}
